import {
    IVanillaReaderContentPositionData,
    VanillaReaderNavigationType
} from '../VanillaReader/VanillaReaderModel';
import {VanillaReaderUiDialog} from './VanillaReaderUiDialog';
import {VanillaUiDialogResultCallback, VanillaUiNavigationIntentEventCallback} from "./VanillaReaderUiModel";

/**
 * # VanillaReaderUiDialogNavigationHistory
 *
 * ## RESPONSIBILITIES
 *
 * This dialog shows a list of the last 10 "go to" events in the book.
 *
 * This component is stateful. It's list state is refreshed by the VanillaReaderUI class whenever new data is available.
 *
 * ## RELATED TYPES
 * - VanillaReaderUiDialogNavigationHistoryController
 */

export class VanillaReaderUiDialogNavigationHistory extends VanillaReaderUiDialog<void> {
    private _list: HTMLElement;

    private _onHistoryItemClickedCallback: VanillaUiNavigationIntentEventCallback | undefined;

    private _bodyStyle = `
        <style>
            #vanilla-reader__dialog-history-items__list {
                padding-inline: 0;
            }

            #vanilla-reader__dialog-history-items__list li {
                min-height: 48px;                
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                align-content: space-between;
                margin-bottom: 0.5em;
                margin-top: 0.5em;
            }
            
            #vanilla-reader__dialog-history-items__list li a {
                vertical-align: super;
                margin-right: 1em;
                margin-top: 1em;
                flex-grow: 1;
            }     

            #vanilla-reader__dialog-history-items__list li button {
                align-self: flex-start;
            }
            
            .vanilla-reader__dialog-history-items__list__item__section-title, .vanilla-reader__dialog-history-items__list__item__section-location {
                /* We set pointer events to none to let the list item receive any key/pointer events */ 
                pointer-events: none;
                display: block;
                font-weight: bold;       
                margin-bottom: 8px;         
            }

            .vanilla-reader__dialog-history-items__list__item__section-location {
                font-weight: normal;                        
            }
            
            .vanilla-reader__dialog-history-items__list__item__selection-text {
                pointer-events: none;
            }
            
            
        </style>`;

    private _bodyTemplate = `<ul id="vanilla-reader__dialog-history-items__list">
        <span class="vanilla-reader__dialog-history-items__list--empty">There are no items yet</span>
    </ul>`;

    private static _dialogTitle: string = 'Navigation History';

    constructor(hostElement: HTMLElement, insertPosition: InsertPosition = 'beforeend') {
        super(hostElement, 'vanilla-reader__dialog-history-items', VanillaReaderUiDialogNavigationHistory._dialogTitle);

        this._dialogElement.insertAdjacentHTML(insertPosition, this._bodyStyle);
        this._dialogBodyElement.innerHTML = this._bodyTemplate;
        this._list = this._dialogElement.querySelector('#vanilla-reader__dialog-history-items__list')!;
    }

    onHistoryItemClicked(callback: VanillaUiNavigationIntentEventCallback): void {
        this._onHistoryItemClickedCallback = callback;
    }

    populate(items: IVanillaReaderContentPositionData[]): void {
        this.empty();
        if (items.length >= 1) {
            items.forEach((item) => {
                let listItem = this.createItem(item);
                this._list.appendChild(listItem);
            });
        } else {
            this._list.innerHTML = '<span class="vanilla-reader__dialog-history-items__list--empty">There are no item yet</span>';
        }
    }

    createItem(historyItem: IVanillaReaderContentPositionData): HTMLLIElement {
        let listItem = this._hostDocument.createElement('li');
        let bookSection = historyItem.navItemData ? historyItem.navItemData[0]?.title : '';
        listItem.setAttribute('tabindex', '-1');
        listItem.setAttribute('data-booksection', bookSection);
        listItem.setAttribute('data-contentblocklocator', historyItem.locator || '');

        let listItemAnchor = this._hostDocument.createElement('a');
        listItemAnchor.setAttribute('data-contentblocklocator', historyItem.locator || '');
        listItemAnchor.addEventListener('click', (ev) => {
            this._event_listItemClicked(ev);
        });

        listItem.addEventListener('keyup', (ev) => {
            if (ev.key === 'Enter' || ev.key === 'Space') {
                this._event_listItemClicked(ev);
            }
        });

        listItemAnchor.innerHTML = `
            <span class="vanilla-reader__dialog-history-items__list__item__section-title" aria-label="Book section" title="Book section">${bookSection || ''} (at location ${historyItem.syntheticPageData ? historyItem.syntheticPageData[0]?.label : 'unknown' })</span>                     
            <span class="vanilla-reader__dialog-history-items__list__item__selection-text" aria-label="Text extract" title="Extract">${historyItem.contentBlockData?.textContent.slice(0, 150) || ''}...</span>           
        `;

        listItemAnchor.setAttribute('tabindex', '0');
        listItem.appendChild(listItemAnchor);

        return listItem;
    }

    show(silent?: boolean, returnFocusOnClose: boolean = true, onDialogCloseCallbackOverride?: VanillaUiDialogResultCallback<void>): void {
        super.show(silent, returnFocusOnClose, onDialogCloseCallbackOverride);
        this.title = VanillaReaderUiDialogNavigationHistory._dialogTitle;
    }

    empty(): void {
        if (this._list.childElementCount > 0) {
            let firstListItem = this._list.firstChild;
            while (firstListItem) {
                firstListItem.remove();
                firstListItem = this._list.firstChild;
            }
        }
    }

    /*
    *
    * EVENT HANDLERS
    *
    * */

    private _event_listItemClicked = (ev: Event) => {
        let locator = (ev.target as HTMLElement).getAttribute('data-contentblocklocator') || (ev.target as HTMLElement).parentElement!.getAttribute('data-contentblocklocator');
        if (locator && this._onHistoryItemClickedCallback) {
            this._onHistoryItemClickedCallback({
                navigationType: VanillaReaderNavigationType.GOTO,
                locator: locator,
            });
        }
    };

}
